
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import "../styles/Invoices.css";

import {
  FaSearch,
  FaDownload,
  FaTrashAlt,
  FaFilter,
  FaRegCalendarAlt,
  FaPaperPlane,
  FaRegFileAlt,
  FaExclamationCircle,
} from "react-icons/fa";

import { useNavigate, useLocation } from "react-router-dom";
import axios from "../axiosConfig";
import { getSocket } from "../lib/socket";
import FilterInvoicesModal from "./FilterInvoicesModal";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { useReactToPrint } from "react-to-print";
import InvoicePrint from "./InvoicePrint";
import { selectClientInfo } from "../lib/selectClientInfo";
import PurchaseOrders from "./PurchaseOrders";
import {
  writeInvoicesAuxCache,
} from "../lib/invoicesAuxCache";

const PO_CACHE_KEY = "purchase_orders_cache_v1";
const INVOICES_CACHE_KEY = "invoices_summary_cache_v1";

/*
 * LOADEvo Invoices V3.4 — visual demonstration fixtures.
 *
 * Frontend only. No database writes, fiscal issuance,
 * signature, official numbering or PDF operations.
 *
 * Available exclusively on the Dispatcher TEST hostname.
 */

const FISCAL_DEMO_DOCUMENTS = [
  {
    id: "demo-fiscal-1",
    documentNumber: "DEMO-FT-000124",
    documentType: "FT",
    issueDate: "2026-10-20",
    customerName: "Atlantic Freight Logistics",
    sourceLoadId: "LD-00487",
    currency: "EUR",
    netTotal: "2350.00",
    taxPayable: "540.50",
    grossTotal: "2890.50",
    status: "ISSUED",
    atcud: null,
    lines: [
      {
        id: "demo-line-1",
        description: "International road transport",
        quantity: 1,
        unitPrice: "2000.00",
        netAmount: "2000.00",
        taxPercentage: "23.00",
        taxAmount: "460.00",
        grossAmount: "2460.00",
      },
      {
        id: "demo-line-2",
        description: "Fuel surcharge",
        quantity: 1,
        unitPrice: "350.00",
        netAmount: "350.00",
        taxPercentage: "23.00",
        taxAmount: "80.50",
        grossAmount: "430.50",
      },
    ],
  },
  {
    id: "demo-fiscal-2",
    documentNumber: "DEMO-FT-000125",
    documentType: "FT",
    issueDate: "2026-10-19",
    customerName: "Transportes Costa Norte, Lda.",
    sourceLoadId: "LD-00492",
    currency: "EUR",
    netTotal: "1780.00",
    taxPayable: "409.40",
    grossTotal: "2189.40",
    status: "DRAFT",
    atcud: null,
    lines: [
      {
        id: "demo-line-3",
        description: "Road transport — Porto to Madrid",
        quantity: 1,
        unitPrice: "1780.00",
        netAmount: "1780.00",
        taxPercentage: "23.00",
        taxAmount: "409.40",
        grossAmount: "2189.40",
      },
    ],
  },
  {
    id: "demo-fiscal-3",
    documentNumber: "DEMO-NC-000018",
    documentType: "NC",
    issueDate: "2026-10-17",
    customerName: "European Distribution Services",
    sourceLoadId: "LD-00464",
    currency: "EUR",
    netTotal: "-350.00",
    taxPayable: "-80.50",
    grossTotal: "-430.50",
    status: "CANCELLED",
    atcud: null,
    lines: [
      {
        id: "demo-line-4",
        description: "Illustrative transport adjustment",
        quantity: 1,
        unitPrice: "-350.00",
        netAmount: "-350.00",
        taxPercentage: "23.00",
        taxAmount: "-80.50",
        grossAmount: "-430.50",
      },
    ],
  },
  {
    id: "demo-fiscal-4",
    documentNumber: "DEMO-FT-000126",
    documentType: "FT",
    issueDate: "2026-10-15",
    customerName: "International Freight & Distribution Solutions Europe",
    sourceLoadId: "LD-00451",
    currency: "EUR",
    netTotal: "5100.00",
    taxPayable: "1173.00",
    grossTotal: "6273.00",
    status: "ISSUED",
    atcud: null,
    lines: [
      {
        id: "demo-line-5",
        description: "European road freight service",
        quantity: 1,
        unitPrice: "5100.00",
        netAmount: "5100.00",
        taxPercentage: "23.00",
        taxAmount: "1173.00",
        grossAmount: "6273.00",
      },
    ],
  },
];

/* ===================== Helpers ===================== */

const formatDateDots = (dateStr) => {
  if (!dateStr) return "";

  const d = new Date(dateStr);

  if (Number.isNaN(d.getTime())) return dateStr;

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");

  return `${dd}.${mm}.${d.getFullYear()}`;
};

const dateWithin = (dateStr, from, to) => {
  if (!from && !to) return true;
  if (!dateStr || dateStr === "—") return false;

  const v = new Date(dateStr).setHours(0, 0, 0, 0);

  if (Number.isNaN(v)) return false;

  if (from) {
    const fromValue = new Date(from).setHours(0, 0, 0, 0);

    if (!Number.isNaN(fromValue) && v < fromValue) {
      return false;
    }
  }

  if (to) {
    const toValue = new Date(to).setHours(0, 0, 0, 0);

    if (!Number.isNaN(toValue) && v > toValue) {
      return false;
    }
  }

  return true;
};

const parseMoneyValue = (value) => {
  if (value == null || value === "—") return null;

  const raw = String(value)
    .trim()
    .replace(/[^\d.,-]/g, "");

  if (!raw) return null;

  if (raw.includes(",") && raw.includes(".")) {
    return Number(raw.replace(/\./g, "").replace(",", "."));
  }

  if (raw.includes(",")) {
    return Number(raw.replace(",", "."));
  }

  return Number(raw);
};

const toCSV = (rows) => {
  if (!rows.length) return "";

  const headers = Object.keys(rows[0]);

  const esc = (value) => {
    const s = (value ?? "").toString();
    const needs = /[",\n;]|\s$|^\s/.test(s);
    const escaped = s.replace(/"/g, '""');

    return needs ? `"${escaped}"` : escaped;
  };

  return [
    headers.join(","),
    ...rows.map((row) =>
      headers.map((header) => esc(row[header])).join(",")
    ),
  ].join("\n");
};

const download = (filename, mime, content) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.download = filename;

  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);
};

const absoluteFromApiBase = (path) => {
  if (!path) return "";

  if (/^https?:\/\//i.test(path)) return path;

  const base = (axios.defaults?.baseURL || "").replace(/\/$/, "");

  return base
    ? `${base}${path.startsWith("/") ? "" : "/"}${path}`
    : path;
};

function safeISO(dateLike) {
  if (!dateLike) return undefined;

  const date = new Date(dateLike);

  if (Number.isNaN(date.getTime())) return undefined;

  return date.toISOString();
}

const firstNonEmpty = (...candidates) =>
  candidates.find(
    (value) => value != null && String(value).trim() !== ""
  ) ?? "";

const tryShowPicker = (target) => {
  try {
    target?.showPicker?.();
    target?.focus?.();
  } catch {
    // Browser restrictions.
  }
};

const getInvoiceStatusTheme = (status) => {
  switch (String(status || "DRAFT").toUpperCase()) {
    case "SENT":
      return {
        bg: "rgba(59, 130, 246, 0.16)",
        border: "rgba(59, 130, 246, 0.42)",
        color: "#bfdbfe",
      };

    case "PARTIAL":
      return {
        bg: "rgba(245, 158, 11, 0.16)",
        border: "rgba(245, 158, 11, 0.42)",
        color: "#fde68a",
      };

    case "PAID":
      return {
        bg: "rgba(34, 197, 94, 0.16)",
        border: "rgba(34, 197, 94, 0.42)",
        color: "#bbf7d0",
      };

    case "VOID":
      return {
        bg: "rgba(239, 68, 68, 0.16)",
        border: "rgba(239, 68, 68, 0.42)",
        color: "#fecaca",
      };

    case "DRAFT":
    default:
      return {
        bg: "rgba(100, 116, 139, 0.18)",
        border: "rgba(148, 163, 184, 0.35)",
        color: "#dbe4ee",
      };
  }
};

const getInvoiceStatusOptions = (t) => [
  { value: "DRAFT", label: t("status.draft") },
  { value: "SENT", label: t("status.sent") },
  { value: "PARTIAL", label: t("status.partial") },
  { value: "PAID", label: t("status.paid") },
  { value: "VOID", label: t("status.void") },
];

const buildInvoiceStatusSelectStyles = (status) => {
  const theme = getInvoiceStatusTheme(status);

  return {
    control: (base, state) => ({
      ...base,
      minHeight: 34,
      height: 34,
      borderRadius: 8,
      backgroundColor: theme.bg,
      borderColor: state.isFocused ? "#4953ff" : theme.border,
      boxShadow: state.isFocused
        ? "0 0 0 2px rgba(73,83,255,0.15)"
        : "none",
      cursor: "pointer",
      "&:hover": {
        borderColor: state.isFocused ? "#4953ff" : theme.border,
      },
    }),

    valueContainer: (base) => ({
      ...base,
      height: 34,
      padding: "0 8px 0 10px",
    }),

    singleValue: (base) => ({
      ...base,
      color: theme.color,
      fontSize: 13,
      fontWeight: 600,
    }),

    input: (base) => ({
      ...base,
      color: theme.color,
      margin: 0,
      padding: 0,
    }),

    placeholder: (base) => ({
      ...base,
      color: theme.color,
    }),

    indicatorSeparator: () => ({
      display: "none",
    }),

    dropdownIndicator: (base) => ({
      ...base,
      color: "#cbd5e1",
      padding: 6,
      "&:hover": { color: "#ffffff" },
    }),

    menuPortal: (base) => ({
      ...base,
      zIndex: 99999,
    }),

    menu: (base) => ({
      ...base,
      backgroundColor: "#0f1318",
      border: "1px solid #2a3547",
      borderRadius: 10,
      overflow: "hidden",
    }),

    menuList: (base) => ({
      ...base,
      padding: 6,
    }),

    option: (base, state) => ({
      ...base,
      fontSize: 13,
      borderRadius: 8,
      cursor: "pointer",
      backgroundColor: state.isSelected
        ? "#2563eb"
        : state.isFocused
        ? "rgba(255,255,255,0.06)"
        : "transparent",
      color: "#e5e7eb",
    }),
  };
};

/* ===================== Export Modal ===================== */

function ExportInvoicesModal({
  open,
  onClose,
  onConfirm,
  defaultFrom = "",
  defaultTo = "",
  count = 0,
}) {
  const { t } = useTranslation("invoices");

  const T = (key, fallback, options) =>
    t(key, {
      defaultValue: fallback,
      ...(options || {}),
    });

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [format, setFormat] = useState("csv");

  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    setFrom(defaultFrom || "");
    setTo(defaultTo || "");
    setFormat("csv");
  }, [open, defaultFrom, defaultTo]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const labelFrom = T(
    "export.date_from",
    T("export.date_of", "Date from")
  );

  const labelTo = T(
    "export.date_to",
    T("export.date_until", "Date to")
  );

  return (
    <div className="export-modal-backdrop" onMouseDown={onClose}>
      <div
        className="invoices-modal invoices-export-modal"
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="invoices-modal__header">
          <h3 className="invoices-modal__title">
            {T("export.title", "Export Invoices")}
          </h3>

          <button
            type="button"
            className="invoices-modal__close"
            onClick={onClose}
            aria-label={T("common.close", "Close")}
            title={T("common.close", "Close")}
          >
            ✕
          </button>
        </div>

        <div className="invoices-modal__body">
          <div
            style={{
              fontSize: 12,
              opacity: 0.8,
              marginBottom: 10,
            }}
          >
            {T("export.scope", "Rows to export")}:{" "}
            <strong>{count ?? 0}</strong>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginBottom: 6,
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  opacity: 0.85,
                }}
              >
                {labelFrom}
              </label>

              <div style={{ position: "relative" }}>
                <input
                  ref={fromInputRef}
                  type="date"
                  className="load-history-input"
                  value={from}
                  onChange={(event) => setFrom(event.target.value)}
                  onPointerDown={(event) =>
                    tryShowPicker(event.currentTarget)
                  }
                  style={{ width: "100%", paddingRight: 32 }}
                />

                <FaRegCalendarAlt
                  onPointerDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    tryShowPicker(fromInputRef.current);
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    tryShowPicker(fromInputRef.current);
                  }}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "40%",
                    transform: "translateY(-50%)",
                    pointerEvents: "auto",
                    opacity: 0.6,
                    cursor: "pointer",
                  }}
                />
              </div>

              <div
                style={{
                  fontSize: 12,
                  opacity: 0.6,
                  marginTop: 4,
                }}
              >
                {from ? formatDateDots(from) : ""}
              </div>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  opacity: 0.85,
                }}
              >
                {labelTo}
              </label>

              <div style={{ position: "relative" }}>
                <input
                  ref={toInputRef}
                  type="date"
                  className="load-history-input"
                  value={to}
                  onChange={(event) => setTo(event.target.value)}
                  onPointerDown={(event) =>
                    tryShowPicker(event.currentTarget)
                  }
                  style={{ width: "100%", paddingRight: 32 }}
                />

                <FaRegCalendarAlt
                  onPointerDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    tryShowPicker(toInputRef.current);
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    tryShowPicker(toInputRef.current);
                  }}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "40%",
                    transform: "translateY(-50%)",
                    pointerEvents: "auto",
                    opacity: 0.6,
                    cursor: "pointer",
                  }}
                />
              </div>

              <div
                style={{
                  fontSize: 12,
                  opacity: 0.6,
                  marginTop: 4,
                }}
              >
                {to ? formatDateDots(to) : ""}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <div
              style={{
                fontSize: 13,
                opacity: 0.85,
                marginBottom: 6,
              }}
            >
              {T("export.format", "Format")}
            </div>

            <div className="invoices-modal__options">
              {[
                {
                  value: "csv",
                  label: T("export.formats.csv", "CSV"),
                },
                {
                  value: "xls",
                  label: T("export.formats.xls", "Excel (XLSX)"),
                },
                {
                  value: "pdf",
                  label: T("export.formats.pdf", "PDF"),
                },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`invoices-modal__option ${
                    format === option.value ? "is-active" : ""
                  }`}
                  onClick={() => setFormat(option.value)}
                >
                  <span>{option.label}</span>

                  {format === option.value && (
                    <span className="invoices-modal__check">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="invoices-modal__footer">
          <button
            className="load-history-btn"
            onClick={onClose}
            type="button"
          >
            {T("common.cancel", "Cancel")}
          </button>

          <button
            className="load-history-btn load-history-btn-primary"
            onClick={() => onConfirm({ from, to, format })}
            disabled={!count}
            style={
              !count
                ? { opacity: 0.6, pointerEvents: "none" }
                : undefined
            }
            type="button"
          >
            {T("export.export", "Export")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===================== Cache ===================== */

const viewFromUrl = (search) => {
  const value = new URLSearchParams(search || "").get("view");

  return value === "purchase_orders"
    ? "purchase_orders"
    : "invoices";
};

function persistInvoicesCache({
  items,
  total,
  nextCursor,
  hasMore,
}) {
  try {
    localStorage.setItem(
      INVOICES_CACHE_KEY,
      JSON.stringify({
        ts: Date.now(),
        items: items || [],
        total: typeof total === "number" ? total : 0,
        nextCursor: nextCursor ?? null,
        hasMore: !!hasMore,
      })
    );
  } catch {
    // Ignore storage errors.
  }
}

function readInvoicesCache() {
  try {
    const raw = localStorage.getItem(INVOICES_CACHE_KEY);

    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (!parsed || !Array.isArray(parsed.items)) {
      return null;
    }

    return {
      items: parsed.items,
      total: typeof parsed.total === "number" ? parsed.total : 0,
      nextCursor: parsed.nextCursor ?? null,
      hasMore: !!parsed.hasMore,
    };
  } catch {
    return null;
  }
}

/* ===================== Invoices ===================== */

export default function Invoices() {
  const { t, i18n } = useTranslation([
    "invoices",
    "purchase_orders",
  ]);

  const navigate = useNavigate();
  const location = useLocation();

  const fiscalPreviewEnabled =
    window.location.hostname === "test-app.loadevo.com" &&
    new URLSearchParams(location.search).get("fiscalPreview") === "1";

  const fiscalApiMockEnabled =
    window.location.hostname === "test-app.loadevo.com" &&
    new URLSearchParams(location.search).get("fiscalApiMock") === "1" &&
    !fiscalPreviewEnabled;

  const [selectedDocView, setSelectedDocView] = useState(() =>
    viewFromUrl(location.search)
  );

  const isPO = selectedDocView === "purchase_orders";

  const tPO = (key, fallback, options) =>
    t(key, {
      ns: "purchase_orders",
      defaultValue: fallback,
      ...(options || {}),
    });

  /* ===================== Provisional documents ===================== */

  const [cacheInit] = useState(() => readInvoicesCache());

  const [invoices, setInvoices] = useState(
    () => cacheInit?.items || []
  );

  const [totalInvoices, setTotalInvoices] = useState(
    () => cacheInit?.total ?? 0
  );

  const [nextCursor, setNextCursor] = useState(
    () => cacheInit?.nextCursor ?? null
  );

  const [hasMoreInvoices, setHasMoreInvoices] = useState(
    () => cacheInit ? !!cacheInit.hasMore : true
  );

  const [loadingMore, setLoadingMore] = useState(false);
  const [invoicesFetchError, setInvoicesFetchError] = useState(false);
  const [loading, setLoading] = useState(() => !cacheInit);

  const clientOptions = useMemo(
    () => [...new Set(invoices.map((item) => item.client).filter(Boolean))],
    [invoices]
  );

  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  /* ===================== Fiscal documents ===================== */

  const [fiscalDocuments, setFiscalDocuments] = useState([]);
  const [fiscalTotal, setFiscalTotal] = useState(0);
  const [fiscalCursor, setFiscalCursor] = useState(null);
  const [fiscalHasMore, setFiscalHasMore] = useState(false);
  const [fiscalLoading, setFiscalLoading] = useState(true);
  const [fiscalLoadingMore, setFiscalLoadingMore] = useState(false);
  const [fiscalError, setFiscalError] = useState(false);

  const [selectedFiscalId, setSelectedFiscalId] = useState(null);
  const [documentScope, setDocumentScope] = useState("all");

  const [fiscalDetail, setFiscalDetail] = useState(null);
  const [fiscalDetailLoading, setFiscalDetailLoading] = useState(false);
  const [fiscalDetailError, setFiscalDetailError] = useState(false);

  const loadFiscalDocuments = useCallback(
    async ({
      cursor = null,
      append = false,
      signal,
    } = {}) => {
      const params = { limit: 20 };

      if (cursor != null) {
        params.cursor = cursor;
      }

      const { data } = await axios.get("/fiscal-documents", {
        params,
        signal,
        headers: fiscalApiMockEnabled
          ? { "X-LOADEVO-FISCAL-SIMULATION": "1" }
          : {},
      });

      if (
        data?.ok !== true ||
        !Array.isArray(data?.documents)
      ) {
        throw new Error("Invalid fiscal documents response");
      }

      setFiscalDocuments((previous) => {
        if (!append) return data.documents;

        const existing = new Set(
          previous.map((doc) => String(doc.id))
        );

        const incoming = data.documents.filter(
          (doc) => !existing.has(String(doc.id))
        );

        return [...previous, ...incoming];
      });

      setFiscalTotal(Number(data.total) || 0);
      setFiscalCursor(data.nextCursor ?? null);
      setFiscalHasMore(Boolean(data.hasMore));
      setFiscalError(false);
    },
    [fiscalApiMockEnabled]
  );

  useEffect(() => {
    if (selectedDocView !== "invoices") {
      return undefined;
    }

    const controller = new AbortController();

    setFiscalLoading(true);

    loadFiscalDocuments({ signal: controller.signal })
      .catch((error) => {
        if (
          error?.code === "ERR_CANCELED" ||
          controller.signal.aborted
        ) {
          return;
        }

        console.error("Fiscal documents read failed:", error);
        setFiscalError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setFiscalLoading(false);
        }
      });

    return () => controller.abort();
  }, [selectedDocView, loadFiscalDocuments]);

  useEffect(() => {
    if (selectedFiscalId == null) {
      setFiscalDetail(null);
      setFiscalDetailError(false);
      setFiscalDetailLoading(false);
      return undefined;
    }

    const demoDocument = fiscalPreviewEnabled
      ? FISCAL_DEMO_DOCUMENTS.find(
          (doc) => String(doc.id) === String(selectedFiscalId)
        )
      : null;

    if (demoDocument) {
      setFiscalDetail(demoDocument);
      setFiscalDetailError(false);
      setFiscalDetailLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    setFiscalDetail(null);
    setFiscalDetailError(false);
    setFiscalDetailLoading(true);

    axios.get(
      `/fiscal-documents/${encodeURIComponent(selectedFiscalId)}`,
      {
        signal: controller.signal,
        headers: fiscalApiMockEnabled
          ? { "X-LOADEVO-FISCAL-SIMULATION": "1" }
          : {},
      }
    )
      .then(({ data }) => {
        if (data?.ok !== true) {
          throw new Error("Invalid fiscal document detail");
        }

        if (!controller.signal.aborted) {
          setFiscalDetail(data.document ?? null);
        }
      })
      .catch((error) => {
        if (
          error?.code === "ERR_CANCELED" ||
          controller.signal.aborted
        ) {
          return;
        }

        console.error("Fiscal document detail failed:", error);
        setFiscalDetailError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setFiscalDetailLoading(false);
        }
      });

    return () => controller.abort();
  }, [
    selectedFiscalId,
    fiscalPreviewEnabled,
    fiscalApiMockEnabled,
  ]);

  /* ===================== Search and filters ===================== */

  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState("");
  const [poSearchTerm, setPoSearchTerm] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const [filterValues, setFilterValues] = useState({
    status: "all",
    dateFrom: "",
    dateTo: "",
    client: "",
    pickup: "",
    delivery: "",
    minPrice: "",
    maxPrice: "",
  });

  const [showExportModal, setShowExportModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [sendingEmailId, setSendingEmailId] = useState(null);

  const handleDocToggle = (view) => {
    if (view === selectedDocView) return;

    const params = new URLSearchParams(location.search);

    if (view === "purchase_orders") {
      params.set("view", "purchase_orders");
    } else {
      params.delete("view");
    }

    const search = params.toString();

    navigate(
      {
        pathname: "/invoices",
        search: search ? `?${search}` : "",
      },
      { replace: true }
    );

    setSelectedDocView(view);
  };

  /* ===================== PDF ===================== */

  const [printInvoice, setPrintInvoice] = useState(null);
  const [printMode, setPrintMode] = useState(null);
  const [printClient, setPrintClient] = useState(null);
  const [printIssuer, setPrintIssuer] = useState(null);
  const printRef = useRef(null);

  const purchaseOrdersRef = useRef(null);

  const [poStats, setPoStats] = useState({
    total: 0,
    visible: 0,
  });

  const [companyProfile, setCompanyProfile] = useState(null);
  const [companyLoaded, setCompanyLoaded] = useState(false);

  const [clients, setClients] = useState([]);
  const [clientsLoaded, setClientsLoaded] = useState(false);

  const idle = (callback) => {
    if (typeof window.requestIdleCallback === "function") {
      return window.requestIdleCallback(callback, {
        timeout: 1500,
      });
    }

    return window.setTimeout(callback, 300);
  };

  /* ===================== PO prefetch ===================== */

  useEffect(() => {
    let alive = true;

    const handle = idle(async () => {
      try {
        const { data } = await axios.get("/purchaseorders", {
          params: {
            limit: 20,
            offset: 0,
          },
        });

        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.rows)
          ? data.rows
          : Array.isArray(data?.purchaseOrders)
          ? data.purchaseOrders
          : [];

        const total =
          typeof data?.total === "number"
            ? data.total
            : typeof data?.count === "number"
            ? data.count
            : typeof data?.meta?.total === "number"
            ? data.meta.total
            : null;

        const nextOffset = list.length;

        const hasMore =
          typeof data?.hasMore === "boolean"
            ? data.hasMore
            : total != null
            ? nextOffset < total
            : list.length === 20;

        if (!alive) return;

        try {
          sessionStorage.setItem(
            PO_CACHE_KEY,
            JSON.stringify({
              ts: Date.now(),
              items: list,
              meta: {
                total: typeof total === "number" ? total : 0,
                nextOffset,
                hasMore: !!hasMore,
              },
            })
          );
        } catch {
          // Ignore storage errors.
        }
      } catch (error) {
        console.warn("PO prefetch failed:", error);
      }
    });

    return () => {
      alive = false;

      if (typeof handle === "number") {
        clearTimeout(handle);
      }
    };
  }, []);

  /* ===================== Legacy invoice pagination ===================== */

  const invoicesSentinelRef = useRef(null);

  const fetchInvoicesPage = useCallback(
    async ({ cursor = null, mode = "replace" } = {}) => {
      const params = { limit: 20 };

      if (cursor) params.cursor = cursor;

      const { data } = await axios.get("/invoices/summary", {
        params,
      });

      const rows = Array.isArray(data?.invoices)
        ? data.invoices
        : [];

      setInvoicesFetchError(false);

      const total =
        typeof data?.total === "number" ? data.total : 0;

      const newNextCursor = data?.nextCursor ?? null;
      const newHasMore = !!data?.hasMore;

      setTotalInvoices(total);
      setNextCursor(newNextCursor);
      setHasMoreInvoices(newHasMore);

      setInvoices((previous) => {
        const prevByKey = new Map(
          previous.map((item) => [
            String(item.id ?? item.invoiceNumber),
            item,
          ])
        );

        const mergedPage = rows.map((row) => {
          const key = String(
            row.id ?? row.invoiceNumber
          );

          const old = prevByKey.get(key);

          return old
            ? { ...row, status: row.status ?? old.status }
            : row;
        });

        const next =
          mode === "append"
            ? [...previous, ...mergedPage]
            : mergedPage;

        persistInvoicesCache({
          items: next,
          total,
          nextCursor: newNextCursor,
          hasMore: newHasMore,
        });

        return next;
      });
    },
    []
  );

  useEffect(() => {
    if (loading) return;
    if (selectedDocView !== "invoices") return;

    const element = invoicesSentinelRef.current;

    if (!element) return;

    const observer = new IntersectionObserver(
      async (entries) => {
        const first = entries[0];

        if (!first?.isIntersecting) return;
        if (!hasMoreInvoices) return;
        if (loadingMore) return;
        if (!nextCursor) return;

        try {
          setLoadingMore(true);

          await fetchInvoicesPage({
            cursor: nextCursor,
            mode: "append",
          });
        } catch (error) {
          console.error(
            "Failed to load more invoices:",
            error
          );
        } finally {
          setLoadingMore(false);
        }
      },
      {
        root: null,
        rootMargin: "600px 0px",
        threshold: 0,
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [
    loading,
    selectedDocView,
    hasMoreInvoices,
    loadingMore,
    nextCursor,
    fetchInvoicesPage,
  ]);

  /* ===================== Client data ===================== */

  const clientsNorm = useMemo(
    () =>
      (clients || []).map((row) => ({
        ...row,

        name: firstNonEmpty(
          row.name,
          row.companyName,
          row.clientName
        ),

        address: firstNonEmpty(
          row.address,
          row.address1,
          row.street,
          row.streetAddress,
          row.street_address
        ),

        postalCode: firstNonEmpty(
          row.postalCode,
          row.postal_code,
          row.zipCode,
          row.zip,
          row.postcode
        ),

        city: firstNonEmpty(
          row.city,
          row.town,
          row.locality
        ),

        country: firstNonEmpty(
          row.country,
          row.country_name,
          row.countryName
        ),

        email: firstNonEmpty(
          row.email,
          row.contactEmail,
          row.companyEmail,
          row.company_email
        ),

        phone: firstNonEmpty(
          row.phone,
          row.phoneNumber,
          row.phone_number,
          row.contactPhone,
          row.companyPhoneNumber,
          row.company_phone_number
        ),

        VAT_TaxInformation: firstNonEmpty(
          row.VAT_TaxInformation,
          row.vatTaxInformation,
          row.vat_tax_id,
          row.vat,
          row.nif,
          row.taxId
        ),
      })),
    [clients]
  );

  useEffect(() => {
    setSelectedDocView(viewFromUrl(location.search));
  }, [location.search]);

  useEffect(() => {
    if (selectedDocView !== "invoices") return;

    let alive = true;

    const handle = idle(async () => {
      try {
        const [carrierResponse, industryResponse] =
          await Promise.all([
            axios.get("/carriers"),
            axios.get("/industries"),
          ]);

        if (!alive) return;

        const carriers = Array.isArray(carrierResponse.data)
          ? carrierResponse.data
          : carrierResponse.data?.rows || [];

        const industries = Array.isArray(industryResponse.data)
          ? industryResponse.data
          : industryResponse.data?.rows || [];

        const merged = [
          ...carriers.map((client) => ({
            ...client,
            clientType: "carrier",
          })),
          ...industries.map((client) => ({
            ...client,
            clientType: "industry",
          })),
        ];

        setClients(merged);

        writeInvoicesAuxCache({
          companyProfile,
          clients: merged,
        });
      } catch (error) {
        console.warn(
          "Failed to fetch carriers/industries:",
          error
        );

        if (alive) setClients([]);
      } finally {
        if (alive) setClientsLoaded(true);
      }
    });

    return () => {
      alive = false;

      if (typeof handle === "number") {
        clearTimeout(handle);
      }
    };
  }, [selectedDocView]);

  /* ===================== Print ===================== */

  const doPrettyPdf = useReactToPrint({
    content: () => printRef.current,

    documentTitle: `${t("files.document_prefix", {
      defaultValue: "Billing-Document",
    })}-${printInvoice?.invoiceNumber ?? printInvoice?.id}`,

    onAfterPrint: () => {
      setPrintInvoice(null);
      setPrintClient(null);
      setPrintIssuer(null);
      setPrintMode(null);
    },
  });

  useEffect(() => {
    if (!printInvoice || printMode !== "dialog") {
      return undefined;
    }

    let attempts = 0;
    let cancelled = false;

    const tryPrint = () => {
      if (cancelled) return;

      if (printRef.current) {
        doPrettyPdf?.();
        return;
      }

      attempts += 1;

      if (attempts < 10) {
        setTimeout(tryPrint, 50);
      } else {
        console.warn("InvoicePrint ref unavailable.");

        setPrintInvoice(null);
        setPrintMode(null);
      }
    };

    const timer = setTimeout(tryPrint, 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [printInvoice, printMode, doPrettyPdf]);

  /* ===================== Company profile ===================== */

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get("/company-details");

        const row = Array.isArray(data)
          ? data[0]
          : data;

        const base = {
          name: firstNonEmpty(
            row?.company_name,
            row?.name,
            row?.companyName
          ),

          address: firstNonEmpty(
            row?.address,
            row?.address1,
            row?.street,
            row?.streetAddress,
            row?.street_address
          ),

          city: firstNonEmpty(
            row?.city,
            row?.town,
            row?.locality
          ),

          postalCode: firstNonEmpty(
            row?.postal_code,
            row?.zipCode,
            row?.postalCode,
            row?.zip,
            row?.postcode
          ),

          country: firstNonEmpty(
            row?.country,
            row?.country_name,
            row?.countryName
          ),

          email: firstNonEmpty(
            row?.companyEmail,
            row?.company_email,
            row?.email,
            row?.contactEmail
          ),

          phone: firstNonEmpty(
            row?.companyPhoneNumber,
            row?.company_phone_number,
            row?.phoneNumber,
            row?.phone_number,
            row?.phone,
            row?.company_phone,
            row?.contactPhone
          ),

          website: firstNonEmpty(
            row?.website,
            row?.company_website,
            row?.site,
            row?.url
          ),

          vat: firstNonEmpty(
            row?.vat_tax_id,
            row?.vatTaxId,
            row?.vatTaxID,
            row?.VAT_TaxInformation,
            row?.vatTaxInformation,
            row?.company_registration_number,
            row?.companyRegistrationNumber,
            row?.vat,
            row?.vatNumber,
            row?.nif,
            row?.taxId
          ),

          vat_tax_id: firstNonEmpty(
            row?.vat_tax_id,
            row?.vatTaxId,
            row?.vatTaxID,
            row?.VAT_TaxInformation,
            row?.vatTaxInformation,
            row?.company_registration_number,
            row?.companyRegistrationNumber,
            row?.vat,
            row?.vatNumber,
            row?.nif,
            row?.taxId
          ),

          logoUrl: absoluteFromApiBase(
            firstNonEmpty(
              row?.photoCompany,
              row?.photo_company,
              row?.logo,
              row?.logoUrl
            )
          ),

          paymentMethod: firstNonEmpty(
            row?.paymentMethod,
            t("defaults.payment_method")
          ),

          iban: firstNonEmpty(row?.iban),
          bic: firstNonEmpty(row?.bic),
          paymentInfo: firstNonEmpty(row?.paymentInfo),
        };

        try {
          const { data: financial } = await axios.get(
            "/company/financial-information"
          );

          if (financial) {
            base.iban = firstNonEmpty(
              financial.iban,
              base.iban
            );

            base.bic = firstNonEmpty(
              financial.bic,
              financial.swift,
              base.bic
            );
          }
        } catch (error) {
          console.warn(
            "Financial information unavailable:",
            error
          );
        }

        setCompanyProfile(base);

        writeInvoicesAuxCache({
          companyProfile: base,
          clients,
        });
      } catch (error) {
        console.warn(
          "Company profile unavailable:",
          error
        );

        setCompanyProfile(null);
      } finally {
        setCompanyLoaded(true);
      }
    })();
  }, []);

  /* ===================== Provisional PDF export ===================== */

  async function exportRowPdf(invoice) {
    if (!invoice?.id) {
      alert(t("alerts.missing_id"));
      return;
    }

    let issuerForPrint = null;

    let clientForPrint = {
      name: invoice.client || invoice.clientName || "",
      VAT_TaxInformation: "",
      address: "",
      postalCode: "",
      city: "",
      country: "",
      phone: "",
      email: "",
    };

    let invoiceForPrintFromBackend = null;

    try {
      const { data } = await axios.get(
        `/invoices/${invoice.id}/print-context`
      );

      issuerForPrint = data?.issuer || null;

      clientForPrint = {
        ...clientForPrint,
        ...(data?.client || {}),

        VAT_TaxInformation:
          data?.client?.VAT_TaxInformation ||
          data?.client?.vatTaxInformation ||
          data?.client?.vat_tax_id ||
          data?.client?.vat ||
          data?.client?.nif ||
          data?.client?.taxId ||
          clientForPrint.VAT_TaxInformation ||
          "",
      };

      invoiceForPrintFromBackend = data?.invoice || null;
    } catch (error) {
      console.warn(
        "Print context unavailable; using frontend fallback:",
        error
      );

      if (!companyLoaded) {
        alert(t("alerts.company_loading"));
        return;
      }

      if (!clientsLoaded) {
        console.warn("Client records are still loading.");
      }
    }

    try {
      const backendHasClientVat =
        clientForPrint?.VAT_TaxInformation ||
        clientForPrint?.vatTaxInformation ||
        clientForPrint?.vat_tax_id ||
        clientForPrint?.vat ||
        clientForPrint?.nif ||
        clientForPrint?.taxId;

      if (
        !backendHasClientVat &&
        clientsLoaded &&
        Array.isArray(clientsNorm) &&
        clientsNorm.length
      ) {
        const invoiceLike = {
          clientName:
            invoice.client || invoice.clientName || "",

          client:
            invoice.client || invoice.clientName || "",

          clientType: invoice.clientType || "",

          industryClientId:
            invoice.industryClientId ?? null,

          carrierClientId:
            invoice.carrierClientId ?? null,

          clientId:
            invoice.clientId ??
            invoice.industryClientId ??
            invoice.carrierClientId ??
            null,
        };

        const info = selectClientInfo(
          invoiceLike,
          clientsNorm
        );

        if (info) {
          clientForPrint = {
            ...clientForPrint,

            name: firstNonEmpty(
              info.name,
              clientForPrint.name
            ),

            VAT_TaxInformation: firstNonEmpty(
              clientForPrint.VAT_TaxInformation,
              clientForPrint.vatTaxInformation,
              clientForPrint.vat_tax_id,
              clientForPrint.vat,
              clientForPrint.nif,
              clientForPrint.taxId,
              info.VAT_TaxInformation,
              info.vatTaxInformation,
              info.vat_tax_id,
              info.vat,
              info.nif,
              info.taxId
            ),

            address: firstNonEmpty(
              clientForPrint.address,
              info.address
            ),

            postalCode: firstNonEmpty(
              clientForPrint.postalCode,
              info.postalCode
            ),

            city: firstNonEmpty(
              clientForPrint.city,
              info.city
            ),

            country: firstNonEmpty(
              clientForPrint.country,
              info.country
            ),

            phone: firstNonEmpty(
              clientForPrint.phone,
              info.phone,
              info.phoneNumber
            ),

            email: firstNonEmpty(
              clientForPrint.email,
              info.email,
              info.contactEmail
            ),
          };
        }
      }
    } catch (error) {
      console.warn(
        "Failed to resolve print client:",
        error
      );
    }

    setPrintIssuer(issuerForPrint);
    setPrintClient(clientForPrint);

    setPrintMode("download");

    setPrintInvoice({
      ...invoice,
      ...(invoiceForPrintFromBackend || {}),
    });

    let attempts = 0;

    const waitForRef = () =>
      new Promise((resolve, reject) => {
        (function tick() {
          if (printRef.current) {
            return resolve();
          }

          attempts += 1;

          if (attempts > 80) {
            return reject(
              new Error("InvoicePrint render timeout.")
            );
          }

          setTimeout(tick, 25);
        })();
      });

    try {
      await waitForRef();

      const jsPDFmod = await import("jspdf");
      const { default: html2canvas } = await import(
        "html2canvas"
      );

      const jsPDF =
        jsPDFmod.jsPDF ||
        jsPDFmod.default ||
        jsPDFmod;

      const canvas = await html2canvas(
        printRef.current,
        {
          scale: 1.35,
          backgroundColor: "#ffffff",
          useCORS: true,
          logging: false,
          windowWidth: 794,
          imageTimeout: 8000,
        }
      );

      const imgData = canvas.toDataURL(
        "image/jpeg",
        0.82
      );

      const doc = new jsPDF({
        unit: "pt",
        format: "a4",
        compress: true,
      });

      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();

      const ratio = Math.min(
        pageW / canvas.width,
        pageH / canvas.height
      );

      const imgW = canvas.width * ratio;
      const imgH = canvas.height * ratio;

      const x = (pageW - imgW) / 2;
      const y = (pageH - imgH) / 2;

      doc.addImage(
        imgData,
        "JPEG",
        x,
        y,
        imgW,
        imgH,
        undefined,
        "FAST"
      );

      doc.save(
        `${t("files.document_prefix")}-${invoice.invoiceNumber ?? invoice.id}.pdf`
      );
    } catch (error) {
      console.error("PDF generation failed:", error);
      alert(t("alerts.pdf_failed"));
    } finally {
      setPrintInvoice(null);
      setPrintClient(null);
      setPrintIssuer(null);
      setPrintMode(null);
    }
  }

  /* ===================== Delete provisional ===================== */

  async function handleDelete(invoice) {
    const visibleId =
      invoice.id ?? invoice.invoiceNumber;

    if (!visibleId) {
      alert(t("alerts.missing_id"));
      return;
    }

    if (
      !window.confirm(
        t("alerts.delete_confirm", { id: visibleId })
      )
    ) {
      return;
    }

    const previousInvoices = invoices;

    try {
      setDeletingId(visibleId);

      setInvoices((previous) =>
        previous.filter(
          (item) =>
            String(item.id ?? item.invoiceNumber) !==
            String(visibleId)
        )
      );

      if (invoice.id != null) {
        await axios.delete(`/invoices/${invoice.id}`);
      } else {
        throw new Error("Missing invoice id.");
      }
    } catch (error) {
      console.error("Delete failed:", error);

      setInvoices(previousInvoices);
      alert(t("alerts.delete_failed"));
    } finally {
      setDeletingId(null);
    }
  }

  /* ===================== Email provisional ===================== */

  async function handleSendInvoiceEmail(invoice) {
    const visibleId =
      invoice.id ?? invoice.invoiceNumber;

    if (!visibleId || invoice.id == null) {
      alert(t("alerts.missing_id"));
      return;
    }

    try {
      setSendingEmailId(visibleId);

      const { data } = await axios.post(
        `/invoices/${invoice.id}/send-email`
      );

      if (data?.status) {
        const nextStatus = String(
          data.status
        ).toUpperCase();

        setInvoices((previous) => {
          const merged = previous.map((item) => {
            const key = String(
              item.id ?? item.invoiceNumber
            );

            return key === String(visibleId)
              ? { ...item, status: nextStatus }
              : item;
          });

          persistInvoicesCache({
            items: merged,
            total: totalInvoices,
            nextCursor,
            hasMore: hasMoreInvoices,
          });

          return merged;
        });
      }

      alert(
        t("actions.send_email_success", {
          email:
            data?.email ||
            t("v3.email_fallback", {
              defaultValue: "billing email",
            }),
        })
      );
    } catch (error) {
      console.error("Send email failed:", error);

      alert(
        error?.response?.data?.error ||
          t("actions.send_email_failed")
      );
    } finally {
      setSendingEmailId(null);
    }
  }

  /* ===================== Date helpers ===================== */

  function parseYMD(dateLike) {
    if (!dateLike) return null;

    if (typeof dateLike === "string") {
      const match = dateLike.match(
        /^(\d{4})-(\d{2})-(\d{2})/
      );

      if (match) {
        return {
          y: match[1],
          m: match[2],
          d: match[3],
        };
      }
    }

    const date = new Date(dateLike);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return {
      y: String(date.getFullYear()),
      m: String(date.getMonth() + 1).padStart(2, "0"),
      d: String(date.getDate()).padStart(2, "0"),
    };
  }

  function formatDMY(dateLike) {
    const ymd = parseYMD(dateLike);

    return ymd
      ? `${ymd.d}-${ymd.m}-${ymd.y}`
      : "—";
  }

  function formatHHMM(hourLike) {
    if (!hourLike) return "";

    const [hhRaw = "", mmRaw = ""] =
      String(hourLike).split(":");

    const hh = String(hhRaw).padStart(2, "0");
    const mm = String(mmRaw || "00").padStart(2, "0");

    return `${hh}:${mm}`;
  }

  function formatDateTime(dateLike, hourLike) {
    const dateOut = formatDMY(dateLike);
    const timeOut = formatHHMM(hourLike);

    if (dateOut === "—" && !timeOut) {
      return "—";
    }

    if (dateOut !== "—" && timeOut) {
      return `${timeOut}  ${dateOut}`;
    }

    return timeOut || dateOut;
  }

  function inferClientTypeByName(clientName, clientsList) {
    const name = String(clientName || "")
      .trim()
      .toLowerCase();

    if (!name) return "";

    const matches = (clientsList || []).filter(
      (client) =>
        String(client?.name || "")
          .trim()
          .toLowerCase() === name
    );

    if (!matches.length) return "";

    const types = [
      ...new Set(
        matches
          .map((client) => client?.clientType)
          .filter(Boolean)
      ),
    ];

    return types.length === 1
      ? types[0] || ""
      : "";
  }

  function toYYYYMMDD(value) {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toISOString().slice(0, 10);
  }

  function daysDiff(fromYmd, toYmd) {
    if (!fromYmd || !toYmd) return null;

    const fromDate = new Date(fromYmd);
    const toDate = new Date(toYmd);

    if (
      Number.isNaN(fromDate.getTime()) ||
      Number.isNaN(toDate.getTime())
    ) {
      return null;
    }

    const days = Math.round(
      (toDate - fromDate) / (1000 * 60 * 60 * 24)
    );

    return Number.isFinite(days) ? days : null;
  }

  function addDaysYMD(dateStr, days) {
    if (!dateStr || typeof days !== "number") {
      return null;
    }

    const date = new Date(dateStr);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    date.setDate(date.getDate() + days);

    return date.toISOString().slice(0, 10);
  }

  /* ===================== Initial provisional fetch ===================== */

  const didFetchInvoicesRef = useRef(false);

  useEffect(() => {
    if (didFetchInvoicesRef.current) return;

    didFetchInvoicesRef.current = true;

    let alive = true;

    (async () => {
      try {
        await fetchInvoicesPage({
          cursor: null,
          mode: "replace",
        });
      } catch (error) {
        console.error(
          "Failed to fetch invoices:",
          error
        );

        if (alive) {
          setInvoicesFetchError(true);

          setInvoices((previous) =>
            previous?.length ? previous : []
          );
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [fetchInvoicesPage]);

  /* ===================== WebSocket ===================== */

  useEffect(() => {
    const socket = getSocket();

    const shapeForSummary = (row) => {
      const pickupLocation =
        row.pickupLocation ?? row.pickup ?? null;

      const deliveryLocation =
        row.deliveryLocation ?? row.delivery ?? null;

      const pickupExists = !!pickupLocation;
      const deliveryExists = !!deliveryLocation;

      const pickupCount =
        typeof row.pickupCount === "number"
          ? row.pickupCount
          : pickupExists
          ? 1
          : 0;

      const deliveryCount =
        typeof row.deliveryCount === "number"
          ? row.deliveryCount
          : deliveryExists
          ? 1
          : 0;

      const pickupList = Array.isArray(row.pickupList)
        ? row.pickupList
        : pickupExists
        ? [pickupLocation]
        : [];

      const deliveryList = Array.isArray(row.deliveryList)
        ? row.deliveryList
        : deliveryExists
        ? [deliveryLocation]
        : [];

      return {
        id: row.id,

        invoiceNumber:
          row.invoiceNumber ?? row.invoice_id ?? null,

        issueDate:
          row.issueDate ?? row.date ?? null,

        paymentDueDate:
          row.paymentDueDate ?? null,

        net_payment_days_carrier:
          row.net_payment_days_carrier ?? null,

        loadId:
          row.loadId ?? row.load_id ?? null,

        loadNumber:
          row.loadNumber ??
          row.load?.loadNumber ??
          null,

        clientType:
          row.clientType ?? row.client_type ?? null,

        industryClientId:
          row.industryClientId ??
          row.industry_client_id ??
          null,

        carrierClientId:
          row.carrierClientId ??
          row.carrier_client_id ??
          null,

        client: row.client ?? null,
        status: row.status ?? null,

        vatRate:
          row.vatRate ?? row.vat_rate ?? null,

        pickupCount,
        deliveryCount,
        pickupLocation,

        pickupDate:
          row.pickupDate ?? null,

        pickupHour:
          row.pickupHour ?? null,

        deliveryLocation,

        deliveryDate:
          row.deliveryDate ?? null,

        deliveryHour:
          row.deliveryHour ?? null,

        pickupList,
        deliveryList,

        price: row.price ?? null,

        date:
          row.issueDate ?? row.date ?? null,
      };
    };

    const subscribe = () =>
      socket.emit("subscribe:invoices");

    subscribe();

    socket.on("connect", subscribe);
    socket.on("reconnect", subscribe);

    const onCreated = (invoice) =>
      setInvoices((previous) => {
        const shaped = shapeForSummary(invoice);

        const key = String(
          shaped.id ?? shaped.invoiceNumber
        );

        const exists = previous.some(
          (item) =>
            String(
              item.id ?? item.invoiceNumber
            ) === key
        );

        const next = exists
          ? previous
          : [shaped, ...previous];

        persistInvoicesCache({
          items: next,
          total:
            (totalInvoices || 0) +
            (exists ? 0 : 1),
          nextCursor,
          hasMore: hasMoreInvoices,
        });

        return next;
      });

    const onUpdated = (invoice) =>
      setInvoices((previous) => {
        const shaped = shapeForSummary(invoice);

        const key = String(
          shaped.id ?? shaped.invoiceNumber
        );

        return previous.map((item) => {
          if (
            String(
              item.id ?? item.invoiceNumber
            ) !== key
          ) {
            return item;
          }

          return {
            ...item,
            ...shaped,
            status: shaped.status ?? item.status,
          };
        });
      });

    const onDeleted = ({ id }) =>
      setInvoices((previous) =>
        previous.filter(
          (item) =>
            String(
              item.id ?? item.invoiceNumber
            ) !== String(id)
        )
      );

    socket.on("invoice:created", onCreated);
    socket.on("invoice:updated", onUpdated);
    socket.on("invoice:deleted", onDeleted);

    return () => {
      socket.emit("unsubscribe:invoices");

      socket.off("connect", subscribe);
      socket.off("reconnect", subscribe);

      socket.off("invoice:created", onCreated);
      socket.off("invoice:updated", onUpdated);
      socket.off("invoice:deleted", onDeleted);
    };
  }, []);

  /* ===================== Normalized documents ===================== */

  const normalized = invoices.map((invoice) => {
    const baseDate =
      invoice.issueDate ?? invoice.date ?? null;

    const netRaw =
      invoice.net_payment_days_carrier;

    const net = Number.isInteger(netRaw)
      ? netRaw
      : netRaw != null
      ? parseInt(netRaw, 10)
      : null;

    const effectiveDue =
      invoice.paymentDueDate ??
      (
        baseDate && Number.isFinite(net)
          ? addDaysYMD(baseDate, net)
          : null
      );

    const loadDisplay =
      invoice.loadNumber ??
      invoice.loadId ??
      "—";

    return {
      id: invoice.id ?? null,

      loadId: invoice.loadId ?? null,
      loadNumber: invoice.loadNumber ?? null,
      loadDisplay,

      invoiceNumber:
        invoice.invoiceNumber ?? null,

      date:
        invoice.issueDate ??
        invoice.date ??
        "—",

      client: invoice.client ?? "—",

      pickup:
        invoice.pickupLocation ?? "—",

      delivery:
        invoice.deliveryLocation ?? "—",

      price: invoice.price ?? "—",

      status:
        invoice.status || "DRAFT",

      vatRate:
        invoice.vatRate ??
        invoice.vat_rate ??
        null,

      expiryOption: null,

      clientType:
        invoice.clientType ??
        invoice.client_type ??
        "",

      industryClientId:
        invoice.industryClientId ??
        invoice.industry_client_id ??
        null,

      carrierClientId:
        invoice.carrierClientId ??
        invoice.carrier_client_id ??
        null,

      pickupDate:
        invoice.pickupDate ?? null,

      pickupHour:
        invoice.pickupHour ?? null,

      deliveryDate:
        invoice.deliveryDate ?? null,

      deliveryHour:
        invoice.deliveryHour ?? null,

      paymentDueDate: effectiveDue,
    };
  });

  /* ===================== Provisional filters ===================== */

  const filteredInvoices = normalized.filter((invoice) => {
    const term = String(invoiceSearchTerm || "")
      .trim()
      .toLowerCase();

    const matchesSearch =
      !term ||
      String(invoice.invoiceNumber ?? "")
        .toLowerCase()
        .includes(term) ||
      String(invoice.id ?? "")
        .toLowerCase()
        .includes(term) ||
      String(invoice.loadDisplay ?? "")
        .toLowerCase()
        .includes(term) ||
      String(invoice.client ?? "")
        .toLowerCase()
        .includes(term) ||
      String(invoice.pickup ?? "")
        .toLowerCase()
        .includes(term) ||
      String(invoice.delivery ?? "")
        .toLowerCase()
        .includes(term) ||
      String(invoice.price ?? "")
        .toLowerCase()
        .includes(term) ||
      String(invoice.status ?? "")
        .toLowerCase()
        .includes(term);

    const matchesStatus =
      !filterValues.status ||
      filterValues.status === "all" ||
      String(invoice.status || "DRAFT").toLowerCase() ===
        String(filterValues.status).toLowerCase();

    const matchesClient =
      !filterValues.client ||
      String(invoice.client || "").toLowerCase() ===
        String(filterValues.client).toLowerCase();

    const matchesPickup =
      !filterValues.pickup ||
      String(invoice.pickup || "")
        .toLowerCase()
        .includes(
          String(filterValues.pickup).toLowerCase()
        );

    const matchesDelivery =
      !filterValues.delivery ||
      String(invoice.delivery || "")
        .toLowerCase()
        .includes(
          String(filterValues.delivery).toLowerCase()
        );

    const matchesDate = dateWithin(
      invoice.date,
      filterValues.dateFrom,
      filterValues.dateTo
    );

    const invoicePrice =
      parseMoneyValue(invoice.price);

    const minPrice =
      parseMoneyValue(filterValues.minPrice);

    const maxPrice =
      parseMoneyValue(filterValues.maxPrice);

    const matchesMinPrice =
      minPrice == null ||
      invoicePrice == null ||
      invoicePrice >= minPrice;

    const matchesMaxPrice =
      maxPrice == null ||
      invoicePrice == null ||
      invoicePrice <= maxPrice;

    return (
      matchesSearch &&
      matchesStatus &&
      matchesClient &&
      matchesPickup &&
      matchesDelivery &&
      matchesDate &&
      matchesMinPrice &&
      matchesMaxPrice
    );
  });

  /* ===================== Global export ===================== */

  const doExport = async ({
    from,
    to,
    format,
  }) => {
    // Export is intentionally restricted to provisional documents.
    const scope = filteredInvoices.filter(
      (invoice) =>
        dateWithin(invoice.date, from, to)
    );

    if (!scope.length) {
      alert(t("export.no_rows"));
      return;
    }

    const flat = scope.map((invoice) => ({
      invoiceId:
        invoice.invoiceNumber ?? invoice.id,

      loadId: invoice.loadId,
      date: invoice.date,

      paymentDueDate:
        invoice.paymentDueDate,

      client: invoice.client,
      pickup: invoice.pickup,
      delivery: invoice.delivery,
      price: invoice.price,

      status:
        invoice.status || "DRAFT",

      pickupDateTime:
        formatDateTime(
          invoice.pickupDate,
          invoice.pickupHour
        ),

      deliveryDateTime:
        formatDateTime(
          invoice.deliveryDate,
          invoice.deliveryHour
        ),
    }));

    const stamp = `${
      from || "all"
    }_to_${to || "all"}`;

    if (format === "csv") {
      download(
        `${t("files.export_prefix")}_${stamp}.csv`,
        "text/csv;charset=utf-8;",
        toCSV(flat)
      );

      setShowExportModal(false);
      return;
    }

    if (format === "pdf") {
      try {
        const jsPDFmod = await import("jspdf");
        const autoTableMod = await import(
          "jspdf-autotable"
        );

        const jsPDF =
          jsPDFmod.default ||
          jsPDFmod.jsPDF ||
          jsPDFmod;

        const autoTable =
          autoTableMod.default ||
          autoTableMod.autoTable ||
          autoTableMod;

        const doc = new jsPDF();

        autoTable(doc, {
          head: [Object.keys(flat[0])],
          body: flat.map((row) =>
            Object.values(row)
          ),
          styles: {
            fontSize: 8,
          },
          headStyles: {
            fillColor: [1, 157, 238],
          },
          margin: 10,
        });

        doc.save(
          `${t("files.export_prefix")}_${stamp}.pdf`
        );

        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        alert(t("export.install_pdf"));
      }

      return;
    }

    try {
      const XLSX = await import("xlsx");

      const worksheet =
        XLSX.utils.json_to_sheet(flat);

      const workbook =
        XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Billing Documents"
      );

      XLSX.writeFile(
        workbook,
        `${t("files.export_prefix")}_${stamp}.xlsx`
      );

      setShowExportModal(false);
    } catch (error) {
      console.error(error);
      alert(t("export.install_xlsx"));
    }
  };

  /* ===================== Edit provisional ===================== */

  function handleEditInvoice(invoice, event) {
    if (
      event.target.classList.contains(
        "invoices-action-icon"
      ) ||
      event.target.closest?.(
        ".invoice-status-wrapper"
      )
    ) {
      return;
    }

    const rowPkId = invoice.id;

    if (rowPkId == null) {
      alert(t("alerts.missing_id"));
      return;
    }

    const clientTypeFromRow = String(
      invoice.clientType || ""
    )
      .trim()
      .toLowerCase();

    const inferredClientType = clientTypeFromRow
      ? ""
      : inferClientTypeByName(
          invoice.client,
          clientsNorm
        );

    const finalClientType =
      clientTypeFromRow ||
      inferredClientType ||
      "";

    const issueYmd = toYYYYMMDD(invoice.date);

    const dueYmd = toYYYYMMDD(
      invoice.paymentDueDate
    );

    const expiryDays = daysDiff(
      issueYmd,
      dueYmd
    );

    navigate(`/invoices/edit/${rowPkId}`, {
      state: {
        prefill: {
          id: rowPkId,

          invoiceNumber:
            invoice.invoiceNumber ?? null,

          clientName: invoice.client,
          clientType: finalClientType,

          pickupPlace: invoice.pickup,
          deliveryPlace: invoice.delivery,

          price: invoice.price,
          issueDate: issueYmd,

          expiryDays: Number.isFinite(
            expiryDays
          )
            ? expiryDays
            : null,

          status: invoice.status,
          paymentDueDate: dueYmd,
        },
      },
    });
  }

  /* ===================== Unified workspace ===================== */

  const selectedInvoice = normalized.find(
    (invoice) =>
      String(invoice.id) ===
      String(selectedInvoiceId)
  ) || null;

  const visibleFiscalDocuments = useMemo(
    () =>
      fiscalPreviewEnabled
        ? [...FISCAL_DEMO_DOCUMENTS, ...fiscalDocuments]
        : fiscalDocuments,
    [fiscalPreviewEnabled, fiscalDocuments]
  );

  const filteredFiscalDocuments = useMemo(() => {
    const term = String(invoiceSearchTerm || "")
      .trim()
      .toLowerCase();

    return visibleFiscalDocuments.filter((doc) => {
      if (!term) return true;

      return [
        doc.documentNumber,
        doc.documentType,
        doc.customerName,
        doc.sourceLoadId,
        doc.status,
      ].some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(term)
      );
    });
  }, [visibleFiscalDocuments, invoiceSearchTerm]);

  const showFiscalDocuments =
    documentScope === "all" ||
    documentScope === "fiscal";

  const showProvisionalDocuments =
    documentScope === "all" ||
    documentScope === "provisional";

  const unifiedTotal =
    (showFiscalDocuments
      ? fiscalTotal +
        (fiscalPreviewEnabled ? FISCAL_DEMO_DOCUMENTS.length : 0)
      : 0) +
    (showProvisionalDocuments
      ? totalInvoices
      : 0);

  const unifiedVisible =
    (
      showFiscalDocuments
        ? filteredFiscalDocuments.length
        : 0
    ) +
    (
      showProvisionalDocuments
        ? filteredInvoices.length
        : 0
    );

  const language = String(
    i18n.resolvedLanguage ||
    i18n.language ||
    "pt"
  ).toLowerCase().replace("_", "-").split("-")[0];

  const locale = {
    pt: "pt-PT",
    en: "en-GB",
    fr: "fr-FR",
    es: "es-ES",
  }[language] || "pt-PT";

  const fiscalMoney = (
    value,
    currency = "EUR"
  ) => {
    if (value == null || value === "") {
      return "—";
    }

    const number = Number(value);

    if (!Number.isFinite(number)) {
      return "—";
    }

    try {
      if (
        language === "pt" &&
        String(currency).toUpperCase() === "EUR"
      ) {
        const amount = new Intl.NumberFormat("de-DE", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          useGrouping: true,
        }).format(number);

        return `${amount}\u00A0€`;
      }

      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        useGrouping: true,
      }).format(number);
    } catch {
      return "—";
    }
  };

  const openInvoiceDetails = (invoice) => {
    if (invoice?.id == null) return;

    setSelectedFiscalId(null);
    setSelectedInvoiceId(invoice.id);
    setDetailsOpen(true);
  };

  const openFiscalDetails = (doc) => {
    if (doc?.id == null) return;

    setDetailsOpen(false);
    setSelectedFiscalId(doc.id);
  };

  useEffect(() => {
    if (!detailsOpen && selectedFiscalId == null) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;

      setDetailsOpen(false);
      setSelectedFiscalId(null);
    };

    window.addEventListener(
      "keydown",
      onKeyDown
    );

    return () =>
      window.removeEventListener(
        "keydown",
        onKeyDown
      );
  }, [detailsOpen, selectedFiscalId]);

  const visibleInvoiceStats = useMemo(() => {
    const active = filteredInvoices.filter(
      (invoice) =>
        !["VOID", "CANCELLED"].includes(
          String(
            invoice.status || ""
          ).toUpperCase()
        )
    );

    const value = (invoice) =>
      Number(
        String(
          invoice.price ?? 0
        ).replace(",", ".")
      ) || 0;

    return {
      net: active.reduce(
        (sum, invoice) =>
          sum + value(invoice),
        0
      ),

      pending: active.filter(
        (invoice) =>
          ["SENT", "ISSUED"].includes(
            String(
              invoice.status || ""
            ).toUpperCase()
          )
      ).length,

      drafts: active.filter(
        (invoice) =>
          String(
            invoice.status || "DRAFT"
          ).toUpperCase() === "DRAFT"
      ).length,

      count: filteredInvoices.length,
    };
  }, [filteredInvoices]);

  const formatInvoiceMoney = (value) =>
    fiscalMoney(
      Number(value) || 0,
      "EUR"
    );

  const totalCount = isPO
    ? poStats.total
    : unifiedTotal;

  const visibleCount = isPO
    ? poStats.visible
    : unifiedVisible;

  const companyDefaults = {
    name: t("defaults.company_name"),
    address: "",
    postalCode: "",
    city: "",
    country: "",
    phone: "",
    email: "",
    website: "",
    vat: "",
    vat_tax_id: "",
    logoUrl: "",
    paymentMethod: t(
      "defaults.payment_method"
    ),
    iban: "",
    bic: "",
    paymentInfo: "",
  };

  const companyForPrint = {
    ...companyDefaults,
    ...(companyProfile || {}),
  };

  const hasActiveInvoiceFilters =
    filterValues.status !== "all" ||
    !!filterValues.dateFrom ||
    !!filterValues.dateTo ||
    !!filterValues.client ||
    !!filterValues.pickup ||
    !!filterValues.delivery ||
    !!filterValues.minPrice ||
    !!filterValues.maxPrice;

  const hasInvoiceSearch =
    String(invoiceSearchTerm || "")
      .trim()
      .length > 0;

  const hasInvoiceSearchOrFilters =
    hasInvoiceSearch ||
    (
      showProvisionalDocuments &&
      hasActiveInvoiceFilters
    );

  const hasAnyInvoices =
    normalized.length > 0;

  const hasVisibleRecords =
    (
      showFiscalDocuments &&
      filteredFiscalDocuments.length > 0
    ) ||
    (
      showProvisionalDocuments &&
      filteredInvoices.length > 0
    );

  const waitingForRecords =
    (
      showFiscalDocuments &&
      fiscalLoading
    ) ||
    (
      showProvisionalDocuments &&
      loading
    );

  const provisionalOnlyError =
    documentScope === "provisional" &&
    invoicesFetchError &&
    !hasAnyInvoices;

  const emptyTitle = hasInvoiceSearchOrFilters
    ? t("empty.no_results_title")
    : documentScope === "fiscal"
    ? t("v3.empty.fiscal_title")
    : documentScope === "provisional"
    ? t("v21.empty_title")
    : t("v3.empty.all_title");

  const emptyDescription = hasInvoiceSearchOrFilters
    ? t("empty.no_results_subtitle")
    : documentScope === "fiscal"
    ? t("v3.empty.fiscal_description")
    : documentScope === "provisional"
    ? t("v21.empty_subtitle")
    : t("v3.empty.all_description");

  /* ===================== Render ===================== */

  return (
    <div className="invoices-page">

      {/* Toolbar */}

      <section className="invoices-card invoices-toolbar">
        <div className="invoices-toolbar__left">
          <div className="invoices-toolbar-heading">
            <h1 className="invoices-toolbar-heading__title">
              {isPO
                ? t("header.purchaseOrdersTitle", "Ordens de Compra")
                : t("page.title", "Faturação")}
            </h1>

            <p className="invoices-toolbar-heading__subtitle">
              {isPO
                ? t("header.purchaseOrdersRegistered", {
                    count: totalCount,
                    defaultValue: "{{count}} ordens de compra registadas",
                  })
                : t("header.invoicesRegistered", {
                    count: totalCount,
                    defaultValue: "{{count}} documentos registados",
                  })}
            </p>
          </div>
        </div>

        <div className="invoices-toolbar__center">
          <div
            className="invoices-toggle-container"
            role="tablist"
            aria-label={t("toggle.aria")}
          >
            <div
              className="invoices-toggle-slider"
              data-view={selectedDocView}
              aria-hidden="true"
            />

            <button
              className={`invoices-toggle-button ${
                selectedDocView === "invoices"
                  ? "active"
                  : ""
              }`}
              onClick={() =>
                handleDocToggle("invoices")
              }
              role="tab"
              aria-selected={
                selectedDocView === "invoices"
              }
              aria-controls="invoices-panel-invoices"
              id="invoices-tab-invoices"
              type="button"
            >
              {t("toggle.invoices")}
            </button>

            <button
              className={`invoices-toggle-button ${
                selectedDocView === "purchase_orders"
                  ? "active"
                  : ""
              }`}
              onClick={() =>
                handleDocToggle("purchase_orders")
              }
              role="tab"
              aria-selected={
                selectedDocView === "purchase_orders"
              }
              aria-controls="invoices-panel-purchase-orders"
              id="invoices-tab-purchase-orders"
              type="button"
            >
              {t("toggle.purchase_orders")}
            </button>
          </div>
        </div>

        <div className="invoices-toolbar__right">
          <div className="invoices-search">
            <FaSearch className="invoices-search__icon" />

            <input
              type="text"
              placeholder={
                isPO
                  ? tPO(
                      "toolbar.search_placeholder",
                      "Search purchase orders..."
                    )
                  : t("search.placeholder")
              }
              className="invoices-search__input"
              value={
                isPO
                  ? poSearchTerm
                  : invoiceSearchTerm
              }
              onChange={(event) => {
                const value = event.target.value;

                if (isPO) {
                  setPoSearchTerm(value);

                  purchaseOrdersRef.current?.setSearchTerm?.(
                    value
                  );
                } else {
                  setInvoiceSearchTerm(value);
                }
              }}
              aria-label={
                isPO
                  ? tPO(
                      "search.aria",
                      "Search purchase orders"
                    )
                  : t("search.aria")
              }
            />

            {(isPO
              ? poSearchTerm
              : invoiceSearchTerm) && (
              <button
                type="button"
                className="invoices-search__clear"
                onClick={() => {
                  if (isPO) {
                    setPoSearchTerm("");

                    purchaseOrdersRef.current?.setSearchTerm?.(
                      ""
                    );
                  } else {
                    setInvoiceSearchTerm("");
                  }
                }}
                aria-label={t("search.clear_aria")}
                title={t("search.clear_title")}
              >
                ✕
              </button>
            )}
          </div>

          <div className="invoices-actions">
            <button
              className={`invoices-btn ${
                !isPO &&
                hasActiveInvoiceFilters
                  ? "invoices-btn--active"
                  : ""
              }`}
              title={
                isPO
                  ? tPO("buttons.filter", "Filter")
                  : t("buttons.add_filter")
              }
              onClick={() => {
                if (isPO) {
                  purchaseOrdersRef.current?.openFilter?.();
                } else {
                  setIsFilterModalOpen(true);
                }
              }}
              type="button"
            >
              <FaFilter />

              <span>
                {isPO
                  ? tPO("buttons.filter", "Filter")
                  : t("buttons.add_filter")}
              </span>

              {!isPO && hasActiveInvoiceFilters && (
                <span className="invoices-btn__badge">
                  •
                </span>
              )}
            </button>

            <button
              className="invoices-btn"
              title={
                isPO
                  ? tPO("buttons.export", "Export")
                  : t("buttons.export")
              }
              onClick={() => {
                if (isPO) {
                  purchaseOrdersRef.current?.openExport?.();
                } else {
                  setShowExportModal(true);
                }
              }}
              type="button"
            >
              <FaDownload />

              <span>
                {isPO
                  ? tPO("buttons.export", "Export")
                  : t("buttons.export")}
              </span>
            </button>

            <button
              className="invoices-btn invoices-btn--primary"
              onClick={() => {
                navigate(
                  selectedDocView === "invoices"
                    ? "/invoices/create"
                    : "/purchaseorders/create"
                );
              }}
              type="button"
            >
              {selectedDocView === "invoices"
                ? t("buttons.new_invoice")
                : tPO(
                    "buttons.new_purchase_order",
                    "+ New Purchase Order"
                  )}
            </button>
          </div>
        </div>
      </section>

      {/* Overview — legacy billing indicators */}

      {!isPO && (
        <div
          className="invoices-v2-summary"
          aria-label={t("overview.title")}
        >
          <div className="invoices-v2-metric invoices-v2-metric--value">
            <span>
              {t("overview.provisional_amount")}
            </span>

            <strong>
              {formatInvoiceMoney(
                visibleInvoiceStats.net
              )}
            </strong>
          </div>

          <div className="invoices-v2-metric invoices-v2-metric--sent">
            <span>
              {t("overview.provisional_pending")}
            </span>

            <strong>
              {visibleInvoiceStats.pending}
            </strong>
          </div>

          <div className="invoices-v2-metric invoices-v2-metric--draft">
            <span>
              {t("overview.provisional_drafts")}
            </span>

            <strong>
              {visibleInvoiceStats.drafts}
            </strong>
          </div>

          <div className="invoices-v2-metric invoices-v2-metric--total">
            <span>
              {t("overview.provisional_documents")}
            </span>

            <strong>
              {totalInvoices ??
                visibleInvoiceStats.count}
            </strong>
          </div>
        </div>
      )}

      {/* Legacy loading */}

      {selectedDocView === "invoices" &&
        loading &&
        invoices.length === 0 && (
          <section className="invoices-card invoices-loading-card">
            <div className="invoices-loading">
              {t("loading")}
            </div>
          </section>
        )}

      {/* Purchase Orders */}

      {selectedDocView === "purchase_orders" && (
        <section
          className="invoices-card"
          id="invoices-panel-purchase-orders"
          role="tabpanel"
          aria-labelledby="invoices-tab-purchase-orders"
        >
          <PurchaseOrders
            ref={purchaseOrdersRef}
            onStatsChange={setPoStats}
          />
        </section>
      )}

      {/* Unified Invoices Workspace */}

      {selectedDocView === "invoices" && (
        <section
          className="invoices-card"
          id="invoices-panel-invoices"
          role="tabpanel"
          aria-labelledby="invoices-tab-invoices"
        >
          <div
            className={`invoices-v2-workspace invoices-v3-workspace ${
              (
                detailsOpen &&
                selectedInvoice
              ) ||
              selectedFiscalId != null
                ? "invoices-v2-workspace--selected"
                : ""
            }`}
          >
            {/* Left — Documents */}

            <div className="invoices-v2-list">
              <div className="invoices-v2-list-head invoices-v3-list-head">
                <div>
                  <h2>
                    {t("v3.documents")}
                  </h2>

                  <span>
                    {t("v3.subtitle")}
                  </span>
                </div>

                <div className="invoices-v3-list-controls">
                  <div
                    className="invoices-v3-scope"
                    role="group"
                    aria-label={t("v3.scope.aria")}
                  >
                    {[
                      {
                        value: "all",
                        label: t("v3.scope.all"),
                      },
                      {
                        value: "fiscal",
                        label: t("v3.scope.fiscal"),
                      },
                      {
                        value: "provisional",
                        label: t("v3.scope.provisional"),
                      },
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`invoices-v3-scope__button ${
                          documentScope === option.value
                            ? "is-active"
                            : ""
                        }`}
                        aria-pressed={
                          documentScope === option.value
                        }
                        onClick={() => {
                          setDocumentScope(option.value);
                          setSelectedFiscalId(null);
                          setSelectedInvoiceId(null);
                          setDetailsOpen(false);
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>

                  {fiscalPreviewEnabled && (
                    <span className="invoices-v3-demo-badge">
                      {t("v3.preview.badge", {
                        defaultValue: "DEMO · Dados fictícios",
                      })}
                    </span>
                  )}

                  <span className="invoices-v2-list-count">
                    {visibleCount}{" "}
                    {t("register.visible")}
                  </span>
                </div>
              </div>

              {/* Table */}

              <div className="invoices-table">
                {loading && invoices.length > 0 && (
                  <div
                    className="invoices-loading"
                    style={{ padding: "8px 0" }}
                  >
                    {t("loading")}
                  </div>
                )}

                <div className="invoices-table__header">
                  <div>
                    {t("table.invoice_id")}
                  </div>

                  <div>
                    {t("v3.table.load")}
                  </div>

                  <div>
                    {t("table.date")}
                  </div>

                  <div>
                    {t("table.payment_due")}
                  </div>

                  <div>
                    {t("table.pickup_place")}
                  </div>

                  <div>
                    {t("table.delivery_place")}
                  </div>

                  <div>
                    {t("table.client")}
                  </div>

                  <div>
                    {t("v3.table.total")}
                  </div>

                  <div>
                    {t("table.status")}
                  </div>

                  <div className="invoices-col-actions">
                    {t("table.actions")}
                  </div>
                </div>

                {/* Fiscal API errors */}

                {showFiscalDocuments &&
                  fiscalError && (
                    <div
                      className="invoices-v3-notice"
                      role="alert"
                    >
                      <span>
                        {t("v3.errors.fiscal_list")}
                      </span>

                      <button
                        type="button"
                        onClick={async () => {
                          setFiscalLoading(true);

                          try {
                            await loadFiscalDocuments();
                          } catch (error) {
                            console.error(
                              "Fiscal retry failed:",
                              error
                            );

                            setFiscalError(true);
                          } finally {
                            setFiscalLoading(false);
                          }
                        }}
                      >
                        {t("v21.retry")}
                      </button>
                    </div>
                  )}

                {showFiscalDocuments &&
                  fiscalLoading && (
                    <div className="invoices-loading">
                      {t("v3.loading.fiscal_list")}
                    </div>
                  )}

                {/* Fiscal rows — read only */}

                {showFiscalDocuments &&
                  !fiscalError &&
                  filteredFiscalDocuments.map((doc) => (
                    <div
                      key={`fiscal-${doc.id}`}
                      className={`invoices-row invoices-v3-fiscal-row ${
                        String(selectedFiscalId) ===
                        String(doc.id)
                          ? "invoices-row--selected"
                          : ""
                      }`}
                      onClick={() =>
                        openFiscalDetails(doc)
                      }
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (
                          event.key === "Enter" ||
                          event.key === " "
                        ) {
                          event.preventDefault();
                          openFiscalDetails(doc);
                        }
                      }}
                    >
                      <div
                        title={
                          doc.documentNumber ||
                          String(doc.id)
                        }
                      >
                        <strong>
                          {doc.documentNumber ||
                            `#${doc.id}`}
                        </strong>

                        <small>
                          {String(doc.id).startsWith("demo-fiscal-")
                            ? t("v3.preview.sample", {
                                defaultValue: "DEMO · Exemplo",
                              })
                            : t("v3.scope.fiscal_label")}
                        </small>
                      </div>

                      <div>
                        {doc.sourceLoadId || "—"}
                      </div>

                      <div>
                        {formatDMY(doc.issueDate)}
                      </div>

                      <div>—</div>
                      <div>—</div>
                      <div>—</div>

                      <div
                        title={
                          doc.customerName || ""
                        }
                      >
                        {doc.customerName || "—"}
                      </div>

                      <div>
                        {fiscalMoney(
                          doc.grossTotal,
                          doc.currency
                        )}
                      </div>

                      <div>
                        <span
                          className={`invoices-fiscal-v3__status invoices-fiscal-v3__status--${String(
                            doc.status || ""
                          ).toLowerCase()}`}
                        >
                          {t(
                            `v3.fiscal_status.${String(
                              doc.status || ""
                            ).toLowerCase()}`,
                            {
                              defaultValue:
                                doc.status || "—",
                            }
                          )}
                        </span>
                      </div>

                      <div className="invoices-v3-fiscal-row__type">
                        <FaRegFileAlt />

                        <span>
                          {t("v3.scope.fiscal_label")}
                        </span>
                      </div>
                    </div>
                  ))}

                {/* Provisional rows — legacy operations */}

                {showProvisionalDocuments &&
                  filteredInvoices.map((invoice) => {
                    const rowKey =
                      `inv-${invoice.id ?? "noid"}-` +
                      `${invoice.invoiceNumber ?? "nonum"}-` +
                      `${invoice.loadId ?? "noload"}`;

                    const visibleId =
                      invoice.invoiceNumber ??
                      invoice.id;

                    return (
                      <div
                        className={`invoices-row ${
                          detailsOpen &&
                          String(selectedInvoiceId) ===
                          String(invoice.id)
                            ? "invoices-row--selected"
                            : ""
                        }`}
                        key={rowKey}
                        onClick={() =>
                          openInvoiceDetails(invoice)
                        }
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (
                            event.key === "Enter" ||
                            event.key === " "
                          ) {
                            event.preventDefault();

                            openInvoiceDetails(invoice);
                          }
                        }}
                        style={{
                          position: "relative",
                        }}
                      >
                        <div title={String(visibleId ?? "")}>
                          {visibleId}
                        </div>

                        <div title={String(invoice.loadDisplay ?? "")}>
                          {invoice.loadDisplay}
                        </div>

                        <div title={formatDMY(invoice.date)}>
                          {formatDMY(invoice.date)}
                        </div>

                        <div title={formatDMY(invoice.paymentDueDate)}>
                          {formatDMY(invoice.paymentDueDate)}
                        </div>

                        <div>
                          <div>
                            {invoice.pickup}
                          </div>

                          <div className="invoices-subline">
                            {formatDateTime(
                              invoice.pickupDate,
                              invoice.pickupHour
                            )}
                          </div>
                        </div>

                        <div>
                          <div>
                            {invoice.delivery}
                          </div>

                          <div className="invoices-subline">
                            {formatDateTime(
                              invoice.deliveryDate,
                              invoice.deliveryHour
                            )}
                          </div>
                        </div>

                        <div title={String(invoice.client ?? "")}>
                          {invoice.client}
                        </div>

                        <div title={String(invoice.price ?? "")}>
                          {invoice.price}
                        </div>

                        <div
                          className="invoice-status-wrapper"
                          onClick={(event) =>
                            event.stopPropagation()
                          }
                        >
                          <Select
                            classNamePrefix="invoice-status-react-select"
                            value={getInvoiceStatusOptions(t).find(
                              (option) =>
                                option.value ===
                                String(
                                  invoice.status || "DRAFT"
                                ).toUpperCase()
                            )}
                            options={getInvoiceStatusOptions(t)}
                            isSearchable={false}
                            menuPlacement="bottom"
                            menuPosition="fixed"
                            menuPortalTarget={document.body}
                            styles={buildInvoiceStatusSelectStyles(
                              invoice.status
                            )}
                            onChange={async (selected) => {
                              const next = String(
                                selected?.value || "DRAFT"
                              ).toUpperCase();

                              const key = String(
                                invoice.id ??
                                invoice.invoiceNumber
                              );

                              setInvoices((previous) =>
                                previous.map((item) => {
                                  const itemKey = String(
                                    item.id ??
                                    item.invoiceNumber
                                  );

                                  return itemKey === key
                                    ? {
                                        ...item,
                                        status: next,
                                      }
                                    : item;
                                })
                              );

                              try {
                                let response;

                                if (invoice.id != null) {
                                  response = await axios.put(
                                    `/invoices/${invoice.id}`,
                                    { status: next }
                                  );
                                } else if (
                                  invoice.invoiceNumber != null
                                ) {
                                  response = await axios.put(
                                    `/invoices/by-number/${invoice.invoiceNumber}`,
                                    { status: next }
                                  );
                                } else {
                                  throw new Error(
                                    "Missing invoice identifier."
                                  );
                                }

                                const persisted =
                                  response?.data || {};

                                const persistedStatus =
                                  String(
                                    persisted.status ||
                                    next
                                  ).toUpperCase();

                                setInvoices((previous) => {
                                  const merged = previous.map(
                                    (item) => {
                                      const itemKey = String(
                                        item.id ??
                                        item.invoiceNumber
                                      );

                                      if (itemKey !== key) {
                                        return item;
                                      }

                                      return {
                                        ...item,
                                        ...persisted,
                                        status: persistedStatus,
                                      };
                                    }
                                  );

                                  persistInvoicesCache({
                                    items: merged,
                                    total: totalInvoices,
                                    nextCursor,
                                    hasMore:
                                      hasMoreInvoices,
                                  });

                                  return merged;
                                });
                              } catch (error) {
                                console.error(
                                  "Status update failed:",
                                  error
                                );

                                setInvoices((previous) =>
                                  previous.map((item) => {
                                    const itemKey = String(
                                      item.id ??
                                      item.invoiceNumber
                                    );

                                    return itemKey === key
                                      ? {
                                          ...item,
                                          status: String(
                                            invoice.status || "DRAFT"
                                          ).toUpperCase(),
                                        }
                                      : item;
                                  })
                                );

                                alert(
                                  t("alerts.status_update_failed")
                                );
                              }
                            }}
                          />
                        </div>

                        <div
                          className="invoices-cell invoices-cell--actions"
                          onClick={(event) =>
                            event.stopPropagation()
                          }
                        >
                          <button
                            type="button"
                            className="invoices-icon-btn invoices-icon-btn--export"
                            title={t("actions.export_row")}
                            aria-label={t("actions.export_row")}
                            onClick={() =>
                              exportRowPdf(invoice)
                            }
                          >
                            <FaDownload />
                          </button>

                          <button
                            type="button"
                            className="invoices-icon-btn invoices-icon-btn--email"
                            title={t("actions.send_email")}
                            aria-label={t("actions.send_email")}
                            onClick={() =>
                              handleSendInvoiceEmail(invoice)
                            }
                            style={{
                              opacity:
                                sendingEmailId ===
                                (invoice.id ??
                                  invoice.invoiceNumber)
                                  ? 0.5
                                  : 1,

                              pointerEvents:
                                sendingEmailId ===
                                (invoice.id ??
                                  invoice.invoiceNumber)
                                  ? "none"
                                  : "auto",
                            }}
                          >
                            <FaPaperPlane />
                          </button>

                          <button
                            type="button"
                            className="invoices-icon-btn invoices-icon-btn--danger"
                            title={t("actions.delete")}
                            aria-label={t("actions.delete")}
                            onClick={() =>
                              handleDelete(invoice)
                            }
                            style={{
                              opacity:
                                deletingId ===
                                (invoice.id ??
                                  invoice.invoiceNumber)
                                  ? 0.5
                                  : 1,

                              pointerEvents:
                                deletingId ===
                                (invoice.id ??
                                  invoice.invoiceNumber)
                                  ? "none"
                                  : "auto",
                            }}
                          >
                            <FaTrashAlt />
                          </button>
                        </div>
                      </div>
                    );
                  })}

                {/* Unified empty state */}

                {!hasVisibleRecords &&
                  !waitingForRecords &&
                  !(
                    showFiscalDocuments &&
                    fiscalError &&
                    documentScope === "fiscal"
                  ) && (
                    <div className="invoices-empty">
                      <div
                        className={`invoices-empty__icon ${
                          provisionalOnlyError
                            ? "invoices-empty__icon--warning"
                            : ""
                        }`}
                        aria-hidden="true"
                      >
                        {provisionalOnlyError ? (
                          <FaExclamationCircle />
                        ) : (
                          <FaRegFileAlt />
                        )}
                      </div>

                      <div className="invoices-empty__title">
                        {provisionalOnlyError
                          ? t("v21.load_error_title")
                          : emptyTitle}
                      </div>

                      <div className="invoices-empty__subtitle">
                        {provisionalOnlyError
                          ? t("v21.load_error_subtitle")
                          : emptyDescription}
                      </div>

                      {provisionalOnlyError ? (
                        <button
                          className="invoices-btn invoices-btn--primary invoices-empty__button"
                          type="button"
                          onClick={async () => {
                            setLoading(true);
                            setInvoicesFetchError(false);

                            try {
                              await fetchInvoicesPage({
                                cursor: null,
                                mode: "replace",
                              });
                            } catch (error) {
                              console.error(
                                "Invoice retry failed:",
                                error
                              );

                              setInvoicesFetchError(true);
                            } finally {
                              setLoading(false);
                            }
                          }}
                        >
                          {t("v21.retry")}
                        </button>
                      ) : hasInvoiceSearchOrFilters ? (
                        <button
                          className="invoices-btn invoices-btn--secondary invoices-empty__button"
                          type="button"
                          onClick={() => {
                            setInvoiceSearchTerm("");

                            setFilterValues({
                              status: "all",
                              dateFrom: "",
                              dateTo: "",
                              client: "",
                              pickup: "",
                              delivery: "",
                              minPrice: "",
                              maxPrice: "",
                            });
                          }}
                        >
                          {t("empty.clear_filters")}
                        </button>
                      ) : documentScope !== "fiscal" ? (
                        <button
                          className="invoices-btn invoices-btn--primary invoices-empty__button"
                          type="button"
                          onClick={() =>
                            navigate("/invoices/create")
                          }
                        >
                          {t("buttons.new_invoice")}
                        </button>
                      ) : null}
                    </div>
                  )}

                <div
                  ref={invoicesSentinelRef}
                  style={{ height: 1 }}
                />

                {/* Fiscal pagination */}

                {showFiscalDocuments &&
                  !fiscalError &&
                  fiscalHasMore &&
                  fiscalCursor != null && (
                    <div className="invoices-fiscal-v3__more">
                      <button
                        type="button"
                        className="invoices-btn"
                        disabled={fiscalLoadingMore}
                        onClick={async () => {
                          setFiscalLoadingMore(true);

                          try {
                            await loadFiscalDocuments({
                              cursor: fiscalCursor,
                              append: true,
                            });
                          } catch (error) {
                            console.error(
                              "Fiscal pagination failed:",
                              error
                            );

                            alert(
                              t("v3.errors.fiscal_pagination")
                            );
                          } finally {
                            setFiscalLoadingMore(false);
                          }
                        }}
                      >
                        {fiscalLoadingMore
                          ? t("v3.loading.more")
                          : t("v3.actions.load_more")}
                      </button>
                    </div>
                  )}

                {loadingMore && (
                  <div
                    className="invoices-loading"
                    style={{ padding: "12px 0" }}
                  >
                    {t("loading")}
                  </div>
                )}
              </div>
            </div>

            {/* Right — Permanent detail panel */}

            <aside
              className="invoices-v2-detail"
              aria-label={t("v3.detail.panel_aria")}
            >
              {selectedFiscalId != null ? (
                <div className="invoices-v2-detail-inner invoices-v3-fiscal-detail">
                  <div className="invoices-v2-detail-top">
                    <span>
                      {t("v3.detail.fiscal_document")}
                    </span>

                    <button
                      type="button"
                      className="invoices-v2-close"
                      onClick={() =>
                        setSelectedFiscalId(null)
                      }
                      aria-label={t("common.close")}
                    >
                      ×
                    </button>
                  </div>

                  <div className="invoices-v2-detail-title">
                    {fiscalDetail?.documentNumber ||
                      visibleFiscalDocuments.find(
                        (doc) =>
                          String(doc.id) ===
                          String(selectedFiscalId)
                      )?.documentNumber ||
                      `#${selectedFiscalId}`}
                  </div>

                  <div className="invoices-v35-customer">
                    {fiscalDetail?.customerName ||
                      visibleFiscalDocuments.find(
                        (doc) =>
                          String(doc.id) ===
                          String(selectedFiscalId)
                      )?.customerName ||
                      "—"}
                  </div>

                  <div className="invoices-v2-detail-tags">
                    <span className="invoices-v2-provisional">
                      {String(selectedFiscalId).startsWith("demo-fiscal-")
                        ? t("v3.preview.detail", {
                            defaultValue: "DEMO · Documento fictício",
                          })
                        : "LOADEvo Fiscal"}
                    </span>

                    {fiscalDetail?.status && (
                      <span
                        className={`invoices-fiscal-v3__status invoices-fiscal-v3__status--${String(
                          fiscalDetail.status
                        ).toLowerCase()}`}
                      >
                        {t(
                          `v3.fiscal_status.${String(
                            fiscalDetail.status
                          ).toLowerCase()}`,
                          {
                            defaultValue:
                              fiscalDetail.status,
                          }
                        )}
                      </span>
                    )}
                  </div>

                  {fiscalDetailLoading ? (
                    <div className="invoices-loading">
                      {t("v3.loading.fiscal_detail")}
                    </div>
                  ) : fiscalDetailError ? (
                    <div
                      className="invoices-v3-notice"
                      role="alert"
                    >
                      {t("v3.errors.fiscal_detail")}
                    </div>
                  ) : fiscalDetail ? (
                    <div className="invoices-v35-content">
                      <div className="invoices-v35-hero">
                        <span className="invoices-v35-hero__label">
                          {t("v3.detail.total")}
                        </span>

                        <strong className="invoices-v35-hero__amount">
                          {fiscalMoney(
                            fiscalDetail.grossTotal,
                            fiscalDetail.currency
                          )}
                        </strong>
                      </div>

                      <div className="invoices-v35-meta">
                        <div className="invoices-v35-meta__item">
                          <span>
                            {t("v3.detail.issue_date")}
                          </span>

                          <strong>
                            {formatDMY(fiscalDetail.issueDate)}
                          </strong>
                        </div>

                        <div className="invoices-v35-meta__item">
                          <span>
                            {t("v3.detail.related_load")}
                          </span>

                          <strong>
                            {fiscalDetail.sourceLoadId || "—"}
                          </strong>
                        </div>

                        <div className="invoices-v35-meta__item">
                          <span>
                            {t("v3.detail.type")}
                          </span>

                          <strong>
                            {fiscalDetail.documentType || "—"}
                          </strong>
                        </div>

                        <div className="invoices-v35-meta__item">
                          <span>
                            {t("v3.detail.status")}
                          </span>

                          <strong>
                            {t(
                              `v3.fiscal_status.${String(
                                fiscalDetail.status || ""
                              ).toLowerCase()}`,
                              {
                                defaultValue:
                                  fiscalDetail.status || "—",
                              }
                            )}
                          </strong>
                        </div>
                      </div>

                      <section className="invoices-v35-section">
                        <div className="invoices-v35-section__heading">
                          <h3>
                            {t("v3.detail.line_items")}
                          </h3>

                          <span>
                            {t("v3.detail.items_count", {
                              count: Array.isArray(fiscalDetail.lines)
                                ? fiscalDetail.lines.length
                                : 0,
                            })}
                          </span>
                        </div>

                        {Array.isArray(fiscalDetail.lines) &&
                        fiscalDetail.lines.length > 0 ? (
                          <div className="invoices-v35-lines">
                            <div className="invoices-v35-lines__head">
                              <span>
                                {t("v3.detail.description")}
                              </span>
                              <span>
                                {t("v3.detail.quantity_short")}
                              </span>
                              <span>
                                {t("v3.detail.unit_price")}
                              </span>
                              <span>
                                {t("v3.detail.vat")}
                              </span>
                              <span>
                                {t("v3.detail.total")}
                              </span>
                            </div>

                            {fiscalDetail.lines.map((line, index) => {
                              const demoDescriptionKey = {
                                "demo-line-1": "international_transport",
                                "demo-line-2": "fuel_surcharge",
                                "demo-line-3": "porto_madrid",
                                "demo-line-4": "transport_adjustment",
                                "demo-line-5": "european_freight",
                              }[line.id];

                              return (
                                <div
                                  className="invoices-v35-lines__row"
                                  key={line.id ?? index}
                                >
                                  <span className="invoices-v35-lines__description">
                                    {demoDescriptionKey
                                      ? t(
                                          `v3.preview_lines.${demoDescriptionKey}`
                                        )
                                      : line.description || "—"}
                                  </span>

                                  <span>
                                    {line.quantity ?? "—"}
                                  </span>

                                  <span>
                                    {fiscalMoney(
                                      line.unitPrice,
                                      fiscalDetail.currency
                                    )}
                                  </span>

                                  <span>
                                    {line.taxPercentage != null
                                      ? `${new Intl.NumberFormat(locale, {
                                          maximumFractionDigits: 2,
                                        }).format(Number(line.taxPercentage))}%`
                                      : "—"}
                                  </span>

                                  <strong>
                                    {fiscalMoney(
                                      line.grossAmount,
                                      fiscalDetail.currency
                                    )}
                                  </strong>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="invoices-v35-no-lines">
                            {t("v3.detail.no_lines")}
                          </p>
                        )}
                      </section>

                      <section className="invoices-v35-section">
                        <h3>
                          {t("v3.detail.amounts")}
                        </h3>

                        <div className="invoices-v35-totals">
                          <div>
                            <span>
                              {t("v3.detail.net_amount")}
                            </span>

                            <strong>
                              {fiscalMoney(
                                fiscalDetail.netTotal,
                                fiscalDetail.currency
                              )}
                            </strong>
                          </div>

                          <div>
                            <span>
                              {t("v3.detail.vat")}
                            </span>

                            <strong>
                              {fiscalMoney(
                                fiscalDetail.taxPayable,
                                fiscalDetail.currency
                              )}
                            </strong>
                          </div>

                          <div className="invoices-v35-totals__grand">
                            <span>
                              {t("v3.detail.total")}
                            </span>

                            <strong>
                              {fiscalMoney(
                                fiscalDetail.grossTotal,
                                fiscalDetail.currency
                              )}
                            </strong>
                          </div>
                        </div>
                      </section>

                      <section className="invoices-v35-section invoices-v35-fiscal-info">
                        <h3>
                          {t("v3.detail.fiscal_information")}
                        </h3>

                        <div className="invoices-v35-fiscal-info__row">
                          <span>
                            {t("v3.detail.fiscal_number")}
                          </span>

                          <strong>
                            {fiscalDetail.documentNumber || "—"}
                          </strong>
                        </div>

                        <div className="invoices-v35-fiscal-info__row">
                          <span>ATCUD</span>

                          <strong>
                            {fiscalDetail.atcud ||
                              t("v3.detail.not_available")}
                          </strong>
                        </div>
                      </section>

                      <div className="invoices-v35-readonly">
                        <FaRegFileAlt aria-hidden="true" />

                        <span>
                          {t("v3.detail.read_only_note")}
                        </span>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : detailsOpen && selectedInvoice ? (
                <div className="invoices-v2-detail-inner">
                  <div className="invoices-v2-detail-top">
                    <span>
                      {t("details.document")}
                    </span>

                    <button
                      type="button"
                      className="invoices-v2-close"
                      onClick={() =>
                        setDetailsOpen(false)
                      }
                      aria-label={t("common.close")}
                    >
                      ×
                    </button>
                  </div>

                  <div className="invoices-v2-detail-title">
                    #
                    {selectedInvoice.invoiceNumber ??
                      selectedInvoice.id}
                  </div>

                  <div className="invoices-v2-detail-client">
                    {selectedInvoice.client || "—"}
                  </div>

                  <div className="invoices-v2-detail-amount">
                    {formatInvoiceMoney(
                      selectedInvoice.price
                    )}
                  </div>

                  <div className="invoices-v2-detail-tags">
                    <span
                      className={`invoices-v2-state invoices-v2-state--${String(
                        selectedInvoice.status || "DRAFT"
                      ).toLowerCase()}`}
                    >
                      {t(
                        `status.${String(
                          selectedInvoice.status || "DRAFT"
                        ).toLowerCase()}`,
                        {
                          defaultValue:
                            selectedInvoice.status || "DRAFT",
                        }
                      )}
                    </span>

                    <span className="invoices-v2-provisional">
                      {t("details.provisional")}
                    </span>
                  </div>

                  <div className="invoices-v2-detail-section">
                    <h3>
                      {t("details.overview")}
                    </h3>

                    <dl>
                      <div>
                        <dt>
                          {t("table.date")}
                        </dt>

                        <dd>
                          {formatDMY(
                            selectedInvoice.date
                          )}
                        </dd>
                      </div>

                      <div>
                        <dt>
                          {t("table.payment_due")}
                        </dt>

                        <dd>
                          {formatDMY(
                            selectedInvoice.paymentDueDate
                          )}
                        </dd>
                      </div>

                      <div>
                        <dt>
                          {t("v3.table.load")}
                        </dt>

                        <dd>
                          {selectedInvoice.loadDisplay || "—"}
                        </dd>
                      </div>

                      <div>
                        <dt>
                          {t("table.pickup_place")}
                        </dt>

                        <dd>
                          {selectedInvoice.pickup || "—"}
                        </dd>
                      </div>

                      <div>
                        <dt>
                          {t("table.delivery_place")}
                        </dt>

                        <dd>
                          {selectedInvoice.delivery || "—"}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  <div className="invoices-v2-fiscal-note">
                    <strong>
                      {t("details.fiscal_title")}
                    </strong>

                    <p>
                      {t("details.fiscal_note")}
                    </p>
                  </div>

                  <div className="invoices-v2-detail-actions">
                    <button
                      type="button"
                      className="invoices-btn invoices-btn--primary"
                      onClick={(event) =>
                        handleEditInvoice(
                          selectedInvoice,
                          event
                        )
                      }
                    >
                      {t("details.edit_legacy")}
                    </button>

                    <button
                      type="button"
                      className="invoices-btn"
                      onClick={() =>
                        exportRowPdf(selectedInvoice)
                      }
                    >
                      <FaDownload />{" "}
                      {t("actions.export_row")}
                    </button>
                  </div>
                </div>
              ) : (
                /* V3.3 — simplified empty detail panel */

                <div className="invoices-v3-detail-empty">
                  <div className="invoices-v3-detail-empty__header">
                    <div>
                      <h3>
                        {t("v3.empty_detail.title")}
                      </h3>

                      <span>
                        {t("v3.empty_detail.subtitle")}
                      </span>
                    </div>

                    <FaRegFileAlt aria-hidden="true" />
                  </div>

                  <div className="invoices-v3-detail-empty__content">
                    <div className="invoices-v3-detail-empty__icon">
                      <FaRegFileAlt aria-hidden="true" />
                    </div>

                    <strong>
                      {t("v3.empty_detail.no_selection")}
                    </strong>

                    <p>
                      {t("v3.empty_detail.description")}
                    </p>
                  </div>
                </div>
              )}
            </aside>
          </div>
        </section>
      )}

      {/* Invisible provisional print component */}

      <div
        style={{
          position: "fixed",
          left: -10000,
          top: 0,
          width: 794,
          background: "#ffffff",
        }}
      >
        {printInvoice &&
          (() => {
            const prepared = {
              invoiceId:
                printInvoice.invoiceId ??
                printInvoice.invoiceNumber ??
                printInvoice.id,

              date: safeISO(
                printInvoice.issueDate ??
                printInvoice.date
              ),

              paymentDueDate: safeISO(
                printInvoice.paymentDueDate
              ),

              status: printInvoice.status,

              pickup:
                printInvoice.pickup ??
                printInvoice.pickupLocation ??
                "—",

              delivery:
                printInvoice.delivery ??
                printInvoice.deliveryLocation ??
                "—",

              price: Number(
                printInvoice.price || 0
              ),

              items: [
                {
                  description:
                    `${t("v3.print.transport_service")} ` +
                    `${printInvoice.pickup ?? printInvoice.pickupLocation ?? "—"}` +
                    " → " +
                    `${printInvoice.delivery ?? printInvoice.deliveryLocation ?? "—"}`,

                  qty: 1,

                  price: Number(
                    printInvoice.price || 0
                  ),

                  total: Number(
                    printInvoice.price || 0
                  ),
                },
              ],

              vatRate:
                printInvoice.vatRate ??
                printInvoice.taxRate ??
                printInvoice.iva ??
                null,

              taxRate:
                printInvoice.vatRate ??
                printInvoice.taxRate ??
                printInvoice.iva ??
                null,

              notes: "",
            };

            const clientForPrint =
              printClient || {
                name:
                  printInvoice.client ||
                  printInvoice.clientName ||
                  "",

                VAT_TaxInformation:
                  printInvoice.VAT_TaxInformation ||
                  printInvoice.vatTaxInformation ||
                  printInvoice.vat_tax_id ||
                  printInvoice.nif ||
                  "",
              };

            return (
              <InvoicePrint
                ref={printRef}
                invoice={prepared}
                issuer={
                  printIssuer || companyForPrint
                }
                client={clientForPrint}
              />
            );
          })()}
      </div>

      {/* Export and filter modals */}

      <ExportInvoicesModal
        open={showExportModal}
        onClose={() =>
          setShowExportModal(false)
        }
        onConfirm={doExport}
        defaultFrom={filterValues.dateFrom}
        defaultTo={filterValues.dateTo}
        count={
          filteredInvoices.filter((invoice) =>
            dateWithin(
              invoice.date,
              filterValues.dateFrom,
              filterValues.dateTo
            )
          ).length
        }
      />

      <FilterInvoicesModal
        open={isFilterModalOpen}
        value={filterValues}
        onChange={setFilterValues}
        clients={clientOptions}
        onApply={() =>
          setIsFilterModalOpen(false)
        }
        onClear={() =>
          setFilterValues({
            status: "all",
            dateFrom: "",
            dateTo: "",
            client: "",
            pickup: "",
            delivery: "",
            minPrice: "",
            maxPrice: "",
          })
        }
        onClose={() =>
          setIsFilterModalOpen(false)
        }
      />
    </div>
  );
}
